/**
 * ContextViewer Component
 * Issue #417: View and select context files/data for AI enhancement
 */

import { useState, useCallback, useMemo } from 'react';

interface FileContent {
  filePath: string;
  content: string;
  relevance: string;
  size: number;
  included: boolean;
}

// Additional file added by user
interface AdditionalFile {
  filePath: string;
  content: string;
  size: number;
}

interface ContextViewerProps {
  flowContext: string;
  codeContext: {
    apiCalls: string;
    databaseOps: string;
    evidenceSnippets: string;
    fileContents: FileContent[];
  };
  excludedFiles: Set<string>;
  onToggleFile: (filePath: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** Additional files added by user */
  additionalFiles?: AdditionalFile[];
  /** Callback to add a file */
  onAddFile?: (file: AdditionalFile) => void;
  /** Callback to remove an additional file */
  onRemoveFile?: (filePath: string) => void;
  /** Project path for file browser */
  projectPath?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ContextViewer({
  flowContext,
  codeContext,
  excludedFiles,
  onToggleFile,
  onSelectAll,
  onDeselectAll,
  additionalFiles = [],
  onAddFile,
  onRemoveFile,
}: ContextViewerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['files']));
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [showAddFileInput, setShowAddFileInput] = useState(false);
  const [newFilePath, setNewFilePath] = useState('');

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const totalSize = useMemo(() => {
    const includedFiles = codeContext.fileContents.filter(f => !excludedFiles.has(f.filePath));
    const autoSize = includedFiles.reduce((sum, f) => sum + f.size, 0);
    const additionalSize = additionalFiles.reduce((sum, f) => sum + f.size, 0);
    return autoSize + additionalSize;
  }, [codeContext.fileContents, excludedFiles, additionalFiles]);

  const includedCount = codeContext.fileContents.filter(f => !excludedFiles.has(f.filePath)).length;
  const totalFileCount = includedCount + additionalFiles.length;

  return (
    <div className="space-y-3">
      {/* Flow Context (auto-included, read-only) */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('flow')}
          className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400">
              {expandedSections.has('flow') ? '▼' : '▶'}
            </span>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              Flow Data
            </span>
            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
              Auto-included
            </span>
          </div>
        </button>
        {expandedSections.has('flow') && (
          <div className="p-3 bg-white dark:bg-slate-900">
            <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-48 whitespace-pre-wrap font-mono">
              {flowContext}
            </pre>
          </div>
        )}
      </div>

      {/* API Calls */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('api')}
          className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400">
              {expandedSections.has('api') ? '▼' : '▶'}
            </span>
            <span className={`font-medium ${codeContext.apiCalls ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>
              API Calls
            </span>
            {codeContext.apiCalls ? (
              <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                Auto-included
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                None detected
              </span>
            )}
          </div>
        </button>
        {expandedSections.has('api') && (
          <div className="p-3 bg-white dark:bg-slate-900">
            {codeContext.apiCalls ? (
              <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-32 whitespace-pre-wrap font-mono">
                {codeContext.apiCalls}
              </pre>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                No API calls detected in this flow
              </p>
            )}
          </div>
        )}
      </div>

      {/* Database Operations */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('db')}
          className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400">
              {expandedSections.has('db') ? '▼' : '▶'}
            </span>
            <span className={`font-medium ${codeContext.databaseOps ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>
              Database Operations
            </span>
            {codeContext.databaseOps ? (
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                Auto-included
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                None detected
              </span>
            )}
          </div>
        </button>
        {expandedSections.has('db') && (
          <div className="p-3 bg-white dark:bg-slate-900">
            {codeContext.databaseOps ? (
              <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-32 whitespace-pre-wrap font-mono">
                {codeContext.databaseOps}
              </pre>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                No database operations detected in this flow
              </p>
            )}
          </div>
        )}
      </div>

      {/* Evidence Snippets */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('evidence')}
          className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400">
              {expandedSections.has('evidence') ? '▼' : '▶'}
            </span>
            <span className={`font-medium ${codeContext.evidenceSnippets ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>
              Code Evidence
            </span>
            {codeContext.evidenceSnippets ? (
              <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 rounded">
                Auto-included
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                None detected
              </span>
            )}
          </div>
        </button>
        {expandedSections.has('evidence') && (
          <div className="p-3 bg-white dark:bg-slate-900">
            {codeContext.evidenceSnippets ? (
              <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-48 whitespace-pre-wrap font-mono">
                {codeContext.evidenceSnippets}
              </pre>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                No code evidence snippets available for this flow
              </p>
            )}
          </div>
        )}
      </div>

      {/* Source Files (Deep Mode - Selectable) */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800">
          <button
            onClick={() => toggleSection('files')}
            className="flex items-center gap-2"
          >
            <span className="text-slate-400">
              {expandedSections.has('files') ? '▼' : '▶'}
            </span>
            <span className={`font-medium ${(codeContext.fileContents.length > 0 || additionalFiles.length > 0) ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>
              Source Files
            </span>
            {(codeContext.fileContents.length > 0 || additionalFiles.length > 0) ? (
              <span className="text-xs text-slate-500">
                ({totalFileCount} files, ~{formatBytes(totalSize)})
                {additionalFiles.length > 0 && (
                  <span className="ml-1 text-green-600 dark:text-green-400">+{additionalFiles.length} added</span>
                )}
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                No files
              </span>
            )}
          </button>
          <div className="flex gap-2">
            {codeContext.fileContents.length > 0 && (
              <>
                <button
                  onClick={onSelectAll}
                  className="text-xs text-purple-600 hover:text-purple-800 dark:text-purple-400"
                >
                  Select All
                </button>
                <span className="text-slate-300">|</span>
                <button
                  onClick={onDeselectAll}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Deselect All
                </button>
                <span className="text-slate-300">|</span>
              </>
            )}
            {onAddFile && (
              <button
                onClick={() => setShowAddFileInput(!showAddFileInput)}
                className="text-xs text-green-600 hover:text-green-800 dark:text-green-400"
              >
                + Add File
              </button>
            )}
          </div>
        </div>
        {expandedSections.has('files') && (
          <div className="p-3 bg-white dark:bg-slate-900 space-y-2 max-h-80 overflow-auto">
            {/* Add File Input */}
            {showAddFileInput && onAddFile && (
              <div className="p-2 border border-green-200 dark:border-green-800 rounded bg-green-50 dark:bg-green-900/20">
                <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">
                  Add Additional Source File
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFilePath}
                    onChange={(e) => setNewFilePath(e.target.value)}
                    placeholder="Enter file path (e.g., src/components/MyComponent.tsx)"
                    className="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:ring-1 focus:ring-green-500"
                  />
                  <button
                    onClick={async () => {
                      if (newFilePath.trim()) {
                        // The parent component will handle loading the file content
                        onAddFile({
                          filePath: newFilePath.trim(),
                          content: '', // Will be loaded by parent
                          size: 0,
                        });
                        setNewFilePath('');
                        setShowAddFileInput(false);
                      }
                    }}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setNewFilePath('');
                      setShowAddFileInput(false);
                    }}
                    className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Path can be relative to project root or absolute
                </p>
              </div>
            )}

            {/* Additional Files (User Added) */}
            {additionalFiles.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                  <span>+</span> Additional Files ({additionalFiles.length})
                </div>
                {additionalFiles.map((file) => (
                  <div key={`additional-${file.filePath}`} className="border border-green-200 dark:border-green-800 rounded bg-green-50/50 dark:bg-green-900/10">
                    <div className="flex items-center gap-2 p-2">
                      <button
                        onClick={() => setExpandedFile(expandedFile === `additional-${file.filePath}` ? null : `additional-${file.filePath}`)}
                        className="flex-1 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">
                            {expandedFile === `additional-${file.filePath}` ? '▼' : '▶'}
                          </span>
                          <span className="text-sm font-mono text-slate-700 dark:text-slate-300">
                            {file.filePath.split('/').pop()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{formatBytes(file.size)}</span>
                          <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                            Added
                          </span>
                        </div>
                      </button>
                      {onRemoveFile && (
                        <button
                          onClick={() => onRemoveFile(file.filePath)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Remove file"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {expandedFile === `additional-${file.filePath}` && file.content && (
                      <div className="p-2 border-t border-green-200 dark:border-green-800">
                        <div className="text-xs text-slate-500 mb-1 truncate">
                          {file.filePath}
                        </div>
                        <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-48 whitespace-pre-wrap font-mono bg-slate-50 dark:bg-slate-900 p-2 rounded">
                          {file.content.slice(0, 2000)}{file.content.length > 2000 ? '\n... (truncated)' : ''}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Auto-detected Files */}
            {codeContext.fileContents.length > 0 && (
              <div className="space-y-2">
                {additionalFiles.length > 0 && (
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Auto-detected Files ({codeContext.fileContents.length})
                  </div>
                )}
                {codeContext.fileContents.map((file) => {
                  const isExcluded = excludedFiles.has(file.filePath);
                  return (
                    <div key={file.filePath} className="border border-slate-200 dark:border-slate-700 rounded">
                      <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800">
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          onChange={() => onToggleFile(file.filePath)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <button
                          onClick={() => setExpandedFile(expandedFile === file.filePath ? null : file.filePath)}
                          className="flex-1 flex items-center justify-between text-left"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {expandedFile === file.filePath ? '▼' : '▶'}
                            </span>
                            <span className={`text-sm font-mono ${isExcluded ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-300'}`}>
                              {file.filePath.split('/').pop()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{formatBytes(file.size)}</span>
                            <span className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded">
                              {file.relevance}
                            </span>
                          </div>
                        </button>
                      </div>
                      {expandedFile === file.filePath && (
                        <div className="p-2 border-t border-slate-200 dark:border-slate-700">
                          <div className="text-xs text-slate-500 mb-1 truncate">
                            {file.filePath}
                          </div>
                          <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-48 whitespace-pre-wrap font-mono bg-slate-50 dark:bg-slate-900 p-2 rounded">
                            {file.content.slice(0, 2000)}{file.content.length > 2000 ? '\n... (truncated)' : ''}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {codeContext.fileContents.length === 0 && additionalFiles.length === 0 && !showAddFileInput && (
              <div className="text-center py-4">
                <p className="text-xs text-slate-400 dark:text-slate-500 italic mb-2">
                  No source files available.
                </p>
                {onAddFile && (
                  <button
                    onClick={() => setShowAddFileInput(true)}
                    className="text-xs text-green-600 hover:text-green-800 dark:text-green-400"
                  >
                    + Add a file manually
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContextViewer;
